import {
  createRoute,
  useNavigate,
  useRouter,
  useSearch,
} from '@tanstack/react-router'
import { useContext, useState } from 'react'
import { GoogleLogin } from '@react-oauth/google'
import { LogIn, Mail } from 'lucide-react'
import rootRoute from './rootRoute'
import { Button, Input, errorToast, toast } from '@/components/ui'
import AuthLayout from '@/components/layout/AuthLayout'
import PasswordField from '@/components/PasswordField'
import { LoginContext } from '@/lib/auth'
import { $client } from '@/lib/fetch'

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const search = useSearch({ from: '/login' })
  const router = useRouter()
  const navigate = useNavigate()
  const ctx = useContext(LoginContext)!

  if (ctx.user !== null) {
    router.navigate({ to: safeRedirectTarget(search.go) })
    return null
  }

  const login = async () => {
    setIsLoading(true)
    const resp = await $client
      .POST('/api/auth/login', { body: { email, password } })
      .finally(() => setIsLoading(false))

    if (resp.error != null) {
      errorToast('Échec de la connexion', resp.error)
    } else {
      ctx.login({
        email,
        expiry: resp.data.expiry,
        token: resp.data.session_token,
        admin: resp.data.admin,
        is_oauth: resp.data.is_oauth,
      })
      toast({ color: 'success', title: 'Connecté !' })
    }
  }

  return (
    <AuthLayout
      panelTitle="Heureux de vous revoir"
      panelSub="Connectez-vous pour proposer des événements et suivre vos rassemblements."
    >
      <h1 style={{ fontSize: 'var(--text-h2)', margin: '0 0 8px' }}>
        Se connecter
      </h1>
      <p style={{ fontSize: 15.5, color: 'var(--ink-2)', marginBottom: 28 }}>
        Pas encore de compte ?{' '}
        <a
          href="/signup"
          onClick={(e) => {
            e.preventDefault()
            navigate({ to: '/signup' })
          }}
          style={{ color: 'var(--emerald-700)', fontWeight: 600 }}
        >
          Créer un compte
        </a>
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          login()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <Input
          label="Adresse e-mail"
          type="email"
          iconLeft={<Mail size={18} />}
          placeholder="vous@email.fr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <PasswordField
          label="Mot de passe"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <a
            href="/forgot-password"
            onClick={(e) => {
              e.preventDefault()
              navigate({ to: '/forgot-password' })
            }}
            style={{
              color: 'var(--emerald-700)',
              fontWeight: 600,
              fontSize: 14.5,
            }}
          >
            Mot de passe oublié ?
          </a>
        </div>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          type="submit"
          loading={isLoading}
          iconLeft={<LogIn size={18} />}
        >
          Se connecter
        </Button>
      </form>
      <div className="oumma-rule" style={{ margin: '24px 0' }}>
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>ou</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <GoogleLogin
          onSuccess={ctx.logOAuth}
          onError={() =>
            toast({
              color: 'danger',
              title:
                'Impossible de se connecter avec Google, une erreur est survenue.',
            })
          }
          theme="outline"
          size="large"
        />
      </div>
    </AuthLayout>
  )
}

// Only allow same-origin relative paths as redirect targets to prevent
// `/login?go=//evil.com` from sending freshly-authenticated users off-site.
function safeRedirectTarget(raw: string | undefined): string {
  if (!raw) return '/'
  if (!raw.startsWith('/')) return '/'
  // Reject protocol-relative URLs ("//evil.com") and backslash variants that
  // some browsers normalise as `//evil.com`.
  if (raw.startsWith('//') || raw.startsWith('/\\')) return '/'
  return raw
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search) => {
    return { go: search.go as string | undefined }
  },
}).lazy(() => import('./LoginPage.lazy').then((m) => m.Route))

export default LoginPage
